import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

function getCanvasPoint(event, canvas) {
  const bounds = canvas.getBoundingClientRect();
  const scaleX = canvas.width / bounds.width;
  const scaleY = canvas.height / bounds.height;

  return {
    x: (event.clientX - bounds.left) * scaleX,
    y: (event.clientY - bounds.top) * scaleY,
  };
}

function normalizeRect(startX, startY, endX, endY, width, height) {
  const left = Math.min(startX, endX);
  const top = Math.min(startY, endY);
  const rectWidth = Math.abs(endX - startX);
  const rectHeight = Math.abs(endY - startY);

  if (rectWidth < 8 || rectHeight < 8) return null;

  return {
    left: Math.max(0, Math.min(1, left / width)),
    top: Math.max(0, Math.min(1, top / height)),
    width: Math.max(0, Math.min(1, rectWidth / width)),
    height: Math.max(0, Math.min(1, rectHeight / height)),
  };
}

function denormalizeRect(rect, width, height) {
  return {
    left: rect.left * width,
    top: rect.top * height,
    width: rect.width * width,
    height: rect.height * height,
  };
}

function drawRect(ctx, rect) {
  ctx.save();
  ctx.fillStyle = "rgba(210, 58, 43, 0.24)";
  ctx.strokeStyle = "rgba(177, 45, 34, 0.96)";
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 6]);
  ctx.fillRect(rect.left, rect.top, rect.width, rect.height);
  ctx.strokeRect(rect.left, rect.top, rect.width, rect.height);
  ctx.restore();
}

export default function WatermarkedPdfRedactionWorkspace({
  open,
  pages,
  loading,
  error,
  busy,
  fileName,
  onClose,
  onConfirm,
  totalPageCount = 0,
  hasMorePages = false,
  loadingMorePages = false,
  onLoadMorePages = null,
  loadMoreError = "",
  layout = "modal",
}) {
  const isScreen = layout === "screen";
  const canvasRefs = useRef({});
  const imageRefs = useRef({});
  const canvasWrapRef = useRef(null);
  const pageSectionRefs = useRef({});
  const [rectanglesByPage, setRectanglesByPage] = useState({});
  const [drawingRect, setDrawingRect] = useState(null);
  const [loadedPageMeta, setLoadedPageMeta] = useState({});
  const [selectedPageNumber, setSelectedPageNumber] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [fitMode, setFitMode] = useState("width");

  useEffect(() => {
    if (!open) return;

    setRectanglesByPage({});
    setDrawingRect(null);
    setSelectedPageNumber(null);
    setZoom(1);
    setFitMode("width");
  }, [open]);

  useEffect(() => {
    if (!open || !Array.isArray(pages) || pages.length === 0) return;
    if (!selectedPageNumber) {
      setSelectedPageNumber(pages[0].pageNumber);
      return;
    }
    if (!pages.some((page) => page.pageNumber === selectedPageNumber)) {
      setSelectedPageNumber(pages[0].pageNumber);
    }
  }, [open, pages, selectedPageNumber]);

  useEffect(() => {
    if (!open || isScreen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isScreen, open]);

  useEffect(() => {
    if (!open || !Array.isArray(pages) || pages.length === 0) {
      imageRefs.current = {};
      setLoadedPageMeta({});
      return undefined;
    }

    let cancelled = false;
    const pendingPages = pages.filter((page) => !imageRefs.current[page.pageNumber]);
    if (pendingPages.length === 0) return undefined;

    const nextImages = {};
    const nextMeta = {};

    Promise.all(
      pendingPages.map(
        (page) =>
          new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => {
              nextImages[page.pageNumber] = image;
              nextMeta[page.pageNumber] = {
                width: image.naturalWidth,
                height: image.naturalHeight,
              };
              resolve();
            };
            image.onerror = reject;
            image.src = page.imageDataUrl;
          })
      )
    )
      .then(() => {
        if (cancelled) return;
        imageRefs.current = {
          ...imageRefs.current,
          ...nextImages,
        };
        setLoadedPageMeta((current) => ({
          ...current,
          ...nextMeta,
        }));
      })
      .catch(() => {
        if (cancelled) return;
      });

    return () => {
      cancelled = true;
    };
  }, [open, pages]);

  useEffect(() => {
    if (!open) return;

    pages.forEach((page) => {
      const canvas = canvasRefs.current[page.pageNumber];
      const image = imageRefs.current[page.pageNumber];
      if (!canvas || !image) return;

      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

      const rectangles = rectanglesByPage[page.pageNumber] || [];
      rectangles.forEach((rect) => {
        drawRect(ctx, denormalizeRect(rect, canvas.width, canvas.height));
      });

      if (drawingRect?.pageNumber === page.pageNumber) {
        const previewRect = normalizeRect(
          drawingRect.startX,
          drawingRect.startY,
          drawingRect.currentX,
          drawingRect.currentY,
          canvas.width,
          canvas.height
        );
        if (previewRect) {
          drawRect(ctx, denormalizeRect(previewRect, canvas.width, canvas.height));
        }
      }
    });
  }, [drawingRect, open, pages, rectanglesByPage, loadedPageMeta]);

  const allRectangles = useMemo(
    () =>
      Object.entries(rectanglesByPage).flatMap(([pageNumber, rectangles]) =>
        (rectangles || []).map((rect) => ({
          pageNumber: Number(pageNumber),
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        }))
      ),
    [rectanglesByPage]
  );
  const totalPages = Math.max(Number(totalPageCount || 0), pages.length);
  const loadedPageCount = pages.length;
  const selectedPage = useMemo(
    () =>
      pages.find((page) => page.pageNumber === selectedPageNumber) ||
      pages[0] ||
      null,
    [pages, selectedPageNumber]
  );
  const selectedPageMeta = selectedPage ? loadedPageMeta[selectedPage.pageNumber] : null;
  const selectedPageRectangles = selectedPage ? rectanglesByPage[selectedPage.pageNumber] || [] : [];

  const calculateFitZoom = useCallback((mode, pageMeta) => {
    const wrap = canvasWrapRef.current;
    if (!wrap || !pageMeta?.width || !pageMeta?.height) return null;

    const horizontalPadding = 40;
    const verticalPadding = 40;
    const availableWidth = Math.max(240, wrap.clientWidth - horizontalPadding);
    const availableHeight = Math.max(240, wrap.clientHeight - verticalPadding);
    const widthZoom = availableWidth / pageMeta.width;
    const pageZoom = Math.min(widthZoom, availableHeight / pageMeta.height);
    const nextZoom = mode === "page" ? pageZoom : widthZoom;

    return Math.max(0.5, Math.min(2.25, Number(nextZoom.toFixed(2))));
  }, []);

  const selectPage = useCallback((pageNumber, behavior = "smooth") => {
    if (!pageNumber) return;
    const pageNode = pageSectionRefs.current[pageNumber];
    if (pageNode) {
      pageNode.scrollIntoView({ behavior, block: "start", inline: "nearest" });
    }
    setSelectedPageNumber(pageNumber);
  }, []);

  const handleMainScroll = useCallback(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap || pages.length === 0) return;

    const viewportMidpoint = wrap.scrollTop + wrap.clientHeight / 2;
    let nextPageNumber = selectedPageNumber || pages[0]?.pageNumber || null;
    let closestDistance = Number.POSITIVE_INFINITY;

    pages.forEach((page) => {
      const node = pageSectionRefs.current[page.pageNumber];
      if (!node) return;
      const pageMidpoint = node.offsetTop + node.offsetHeight / 2;
      const distance = Math.abs(pageMidpoint - viewportMidpoint);
      if (distance < closestDistance) {
        closestDistance = distance;
        nextPageNumber = page.pageNumber;
      }
    });

    if (nextPageNumber && nextPageNumber !== selectedPageNumber) {
      setSelectedPageNumber(nextPageNumber);
    }
  }, [pages, selectedPageNumber]);

  const changeZoom = (delta) => {
    setFitMode("manual");
    setZoom((current) => Math.max(0.5, Math.min(2.25, Number((current + delta).toFixed(2)))));
  };

  const loadMorePages = useCallback(async () => {
    if (typeof onLoadMorePages !== "function" || loadingMorePages || !hasMorePages) {
      return [];
    }

    const nextPages = await onLoadMorePages();
    return Array.isArray(nextPages) ? nextPages : [];
  }, [hasMorePages, loadingMorePages, onLoadMorePages]);

  const jumpPage = async (direction) => {
    if (!selectedPage) return;
    const currentIndex = pages.findIndex((page) => page.pageNumber === selectedPage.pageNumber);
    const nextPage = pages[currentIndex + direction];
    if (nextPage) {
      selectPage(nextPage.pageNumber);
      return;
    }

    if (direction > 0 && hasMorePages) {
      const nextPages = await loadMorePages();
      if (nextPages.length > 0) {
        selectPage(nextPages[0].pageNumber, "auto");
      }
    }
  };

  const applyFitMode = (mode) => {
    setFitMode(mode);
    const nextZoom = calculateFitZoom(mode, selectedPageMeta);
    if (nextZoom != null) {
      setZoom(nextZoom);
    }
  };

  const handlePointerDown = (pageNumber) => (event) => {
    if (busy || loading) return;
    const canvas = canvasRefs.current[pageNumber];
    if (!canvas) return;

    const point = getCanvasPoint(event, canvas);
    canvas.setPointerCapture?.(event.pointerId);
    setDrawingRect({
      pageNumber,
      pointerId: event.pointerId,
      startX: point.x,
      startY: point.y,
      currentX: point.x,
      currentY: point.y,
    });
  };

  const handlePointerMove = (pageNumber) => (event) => {
    if (!drawingRect || drawingRect.pageNumber !== pageNumber) return;
    const canvas = canvasRefs.current[pageNumber];
    if (!canvas) return;

    const point = getCanvasPoint(event, canvas);
    setDrawingRect((current) =>
      current
        ? {
            ...current,
            currentX: point.x,
            currentY: point.y,
          }
        : current
    );
  };

  const handlePointerUp = (pageNumber) => (event) => {
    if (!drawingRect || drawingRect.pageNumber !== pageNumber) return;
    const canvas = canvasRefs.current[pageNumber];
    if (!canvas) {
      setDrawingRect(null);
      return;
    }

    canvas.releasePointerCapture?.(drawingRect.pointerId);
    const point = getCanvasPoint(event, canvas);
    const rect = normalizeRect(
      drawingRect.startX,
      drawingRect.startY,
      point.x,
      point.y,
      canvas.width,
      canvas.height
    );

    if (rect) {
      setRectanglesByPage((current) => ({
        ...current,
        [pageNumber]: [...(current[pageNumber] || []), rect],
      }));
    }
    setDrawingRect(null);
  };

  const handlePointerCancel = () => {
    setDrawingRect(null);
  };

  const handleUndoPage = (pageNumber) => {
    setRectanglesByPage((current) => {
      const pageRects = current[pageNumber] || [];
      if (pageRects.length === 0) return current;
      const nextPageRects = pageRects.slice(0, -1);
      if (nextPageRects.length === 0) {
        const next = { ...current };
        delete next[pageNumber];
        return next;
      }
      return {
        ...current,
        [pageNumber]: nextPageRects,
      };
    });
  };

  const handleClearAll = () => {
    setRectanglesByPage({});
    setDrawingRect(null);
  };

  useEffect(() => {
    if (!selectedPageMeta || fitMode === "manual") return;

    const nextZoom = calculateFitZoom(fitMode, selectedPageMeta);
    if (nextZoom == null) return;

    setZoom((current) => (Math.abs(current - nextZoom) < 0.01 ? current : nextZoom));
  }, [calculateFitZoom, fitMode, selectedPageMeta]);

  useEffect(() => {
    if (!selectedPage) return undefined;

    const syncViewport = () => {
      if (fitMode !== "manual") {
        const nextZoom = calculateFitZoom(fitMode, selectedPageMeta);
        if (nextZoom != null) {
          setZoom((current) => (Math.abs(current - nextZoom) < 0.01 ? current : nextZoom));
        }
      }
      handleMainScroll();
    };

    const frame = window.requestAnimationFrame(syncViewport);
    window.addEventListener("resize", syncViewport);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", syncViewport);
    };
  }, [calculateFitZoom, fitMode, handleMainScroll, selectedPage, selectedPageMeta, zoom]);

  if (!open) return null;

  const shellStyle = isScreen ? styles.screenShell : styles.backdrop;
  const cardStyle = isScreen ? { ...styles.card, ...styles.screenCard } : styles.card;
  const viewerToolbarStyle = isScreen
    ? { ...styles.viewerToolbar, ...styles.viewerToolbarScreen }
    : styles.viewerToolbar;
  const viewerEyebrowStyle = isScreen
    ? { ...styles.viewerEyebrow, ...styles.viewerEyebrowScreen }
    : styles.viewerEyebrow;
  const viewerTitleStyle = isScreen
    ? { ...styles.viewerTitle, ...styles.viewerTitleScreen }
    : styles.viewerTitle;
  const bodyStyle = isScreen ? { ...styles.body, ...styles.bodyScreen } : styles.body;
  const sidebarStyle = isScreen ? { ...styles.sidebar, ...styles.sidebarScreen } : styles.sidebar;
  const sidebarHeadStyle = isScreen
    ? { ...styles.sidebarHead, ...styles.sidebarHeadScreen }
    : styles.sidebarHead;
  const sidebarLabelStyle = isScreen
    ? { ...styles.sidebarLabel, ...styles.sidebarLabelScreen }
    : styles.sidebarLabel;
  const sidebarHintStyle = isScreen
    ? { ...styles.sidebarHint, ...styles.sidebarHintScreen }
    : styles.sidebarHint;
  const viewerMetaBarStyle = isScreen
    ? { ...styles.viewerMetaBar, ...styles.viewerMetaBarScreen }
    : styles.viewerMetaBar;
  const pageTitleStyle = isScreen
    ? { ...styles.pageTitle, ...styles.pageTitleScreen }
    : styles.pageTitle;
  const pageMetaStyle = isScreen
    ? { ...styles.pageMeta, ...styles.pageMetaScreen }
    : styles.pageMeta;
  const viewerStageStyle = isScreen
    ? { ...styles.viewerStage, ...styles.viewerStageScreen }
    : styles.viewerStage;
  const canvasWrapStyle = isScreen
    ? { ...styles.canvasWrap, ...styles.canvasWrapScreen }
    : styles.canvasWrap;
  const footerStyle = isScreen ? { ...styles.footer, ...styles.footerScreen } : styles.footer;
  const fileHintStyle = isScreen
    ? { ...styles.fileHint, ...styles.fileHintScreen }
    : styles.fileHint;
  const toolbarPillStyle = isScreen
    ? { ...styles.toolbarPill, ...styles.toolbarPillScreen }
    : styles.toolbarPill;
  const toolbarHintStyle = isScreen
    ? { ...styles.toolbarHint, ...styles.toolbarHintScreen }
    : styles.toolbarHint;
  const ghostButtonStyle = isScreen
    ? { ...styles.ghostButton, ...styles.ghostButtonScreen }
    : styles.ghostButton;
  const cancelButtonStyle = isScreen
    ? { ...styles.cancelButton, ...styles.cancelButtonScreen }
    : styles.cancelButton;
  const submitButtonStyle = isScreen
    ? { ...styles.submitButton, ...styles.submitButtonScreen }
    : styles.submitButton;

  return (
    <div
      style={shellStyle}
      onClick={
        isScreen
          ? undefined
          : (event) => {
              if (event.target === event.currentTarget && !busy) onClose();
            }
      }
    >
      <div style={cardStyle}>
        <div style={viewerToolbarStyle}>
          <div style={styles.viewerBrand}>
            {isScreen && (
              <button type="button" style={styles.screenBackButton} onClick={onClose}>
                {"<"} Back to Profile
              </button>
            )}
            <div style={viewerEyebrowStyle}>Watermarked Biodata</div>
            <div style={styles.viewerTitleRow}>
              <h2 style={viewerTitleStyle}>Draw What To Remove</h2>
            </div>
          </div>

          <div style={styles.viewerControls}>
            <div style={styles.controlGroup}>
              <button
                type="button"
                style={{
                  ...styles.iconButton,
                  ...((!selectedPage || selectedPage.pageNumber === pages[0]?.pageNumber) ? styles.disabledButton : {}),
                }}
                onClick={() => jumpPage(-1)}
                disabled={!selectedPage || selectedPage.pageNumber === pages[0]?.pageNumber}
              >
                {"<"}
              </button>
              <div style={styles.pageIndicator}>
                <strong>{selectedPage?.pageNumber || 0}</strong>
                <span>/ {totalPages}</span>
              </div>
              <button
                type="button"
                style={{
                  ...styles.iconButton,
                  ...((!selectedPage || (selectedPage.pageNumber === pages[pages.length - 1]?.pageNumber && !hasMorePages))
                    ? styles.disabledButton
                    : {}),
                }}
                onClick={() => jumpPage(1)}
                disabled={!selectedPage || (selectedPage.pageNumber === pages[pages.length - 1]?.pageNumber && !hasMorePages)}
              >
                {">"}
              </button>
            </div>

            <div style={styles.controlGroup}>
              <button
                type="button"
                style={{ ...styles.iconButton, ...(zoom <= 0.5 ? styles.disabledButton : {}) }}
                onClick={() => changeZoom(-0.1)}
                disabled={zoom <= 0.5}
              >
                -
              </button>
              <div style={styles.zoomIndicator}>{Math.round(zoom * 100)}%</div>
              <button
                type="button"
                style={{ ...styles.iconButton, ...(zoom >= 2.25 ? styles.disabledButton : {}) }}
                onClick={() => changeZoom(0.1)}
                disabled={zoom >= 2.25}
              >
                +
              </button>
            </div>

            <div style={styles.controlGroup}>
              <button
                type="button"
                style={{
                  ...styles.modeButton,
                  ...(fitMode === "width" ? styles.modeButtonActive : {}),
                }}
                onClick={() => applyFitMode("width")}
              >
                Fit Width
              </button>
              <button
                type="button"
                style={{
                  ...styles.modeButton,
                  ...(fitMode === "page" ? styles.modeButtonActive : {}),
                }}
                onClick={() => applyFitMode("page")}
              >
                Fit Page
              </button>
            </div>
          </div>

          <div style={styles.viewerToolbarActions}>
            <span style={toolbarPillStyle}>
              {loadedPageCount} of {totalPages} page{totalPages === 1 ? "" : "s"} loaded
            </span>
            <span style={toolbarPillStyle}>
              {allRectangles.length} redaction{allRectangles.length === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              style={{
                ...ghostButtonStyle,
                ...(allRectangles.length === 0 || busy ? styles.disabledButton : {}),
              }}
              onClick={handleClearAll}
              disabled={allRectangles.length === 0 || busy}
            >
              Clear All
            </button>
            {!isScreen && (
              <button type="button" style={styles.close} onClick={onClose} disabled={busy}>
                x
              </button>
            )}
          </div>
        </div>

        <div style={bodyStyle}>
          {loading ? (
            <div style={styles.emptyState}>Preparing the biodata pages for redaction...</div>
          ) : error ? (
            <div style={styles.errorState}>{error}</div>
          ) : !selectedPage ? (
            <div style={styles.emptyState}>No preview pages are available for this biodata PDF.</div>
          ) : (
            <>
              <aside style={sidebarStyle}>
                <div style={sidebarHeadStyle}>
                  <div style={sidebarLabelStyle}>Pages</div>
                  <div style={sidebarHintStyle}>
                    Choose a page and draw on the main preview.
                    {totalPages > loadedPageCount ? ` Loaded ${loadedPageCount} of ${totalPages} pages.` : ""}
                  </div>
                </div>
                <div style={styles.thumbnailList}>
                  {pages.map((page) => {
                    const isActive = page.pageNumber === selectedPage.pageNumber;
                    const thumbRectangles = rectanglesByPage[page.pageNumber] || [];

                    return (
                      <button
                        key={page.pageNumber}
                        type="button"
                        style={{
                          ...styles.thumbnailButton,
                          ...(isActive ? styles.thumbnailButtonActive : {}),
                        }}
                        onClick={() => selectPage(page.pageNumber)}
                      >
                        <div style={styles.thumbnailPreviewWrap}>
                          <img
                            src={page.imageDataUrl}
                            alt={`Page ${page.pageNumber}`}
                            style={styles.thumbnailPreview}
                          />
                        </div>
                        <div style={styles.thumbnailMeta}>
                          <strong style={styles.thumbnailTitle}>Page {page.pageNumber}</strong>
                          <span style={styles.thumbnailSub}>
                            {thumbRectangles.length} box{thumbRectangles.length === 1 ? "" : "es"}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div style={styles.sidebarFooter}>
                  {loadMoreError ? <div style={styles.inlineError}>{loadMoreError}</div> : null}
                  {hasMorePages ? (
                    <button
                      type="button"
                      style={{
                        ...ghostButtonStyle,
                        width: "100%",
                        justifyContent: "center",
                        ...(loadingMorePages || busy ? styles.disabledButton : {}),
                      }}
                      onClick={() => {
                        loadMorePages();
                      }}
                      disabled={loadingMorePages || busy}
                    >
                      {loadingMorePages ? "Loading more pages..." : "Load More Pages"}
                    </button>
                  ) : totalPages > 0 ? (
                    <div style={styles.sidebarStatus}>All {totalPages} pages are loaded.</div>
                  ) : null}
                </div>
              </aside>

              <section style={styles.viewerMain}>
                <div style={viewerMetaBarStyle}>
                  <div>
                    <strong style={pageTitleStyle}>Page {selectedPage.pageNumber}</strong>
                    <div style={pageMetaStyle}>
                      {selectedPageMeta
                        ? `${selectedPageMeta.width} x ${selectedPageMeta.height} px`
                        : "Loading canvas..."}
                    </div>
                  </div>
                  <div style={styles.viewerMetaActions}>
                    <span style={toolbarHintStyle}>Drag rectangles directly on the page</span>
                    <button
                      type="button"
                      style={{
                        ...ghostButtonStyle,
                        ...(selectedPageRectangles.length === 0 || busy ? styles.disabledButton : {}),
                      }}
                      onClick={() => handleUndoPage(selectedPage.pageNumber)}
                      disabled={selectedPageRectangles.length === 0 || busy}
                    >
                      Undo Last Box
                    </button>
                  </div>
                </div>

                <div style={viewerStageStyle}>
                  <div
                    ref={canvasWrapRef}
                    style={canvasWrapStyle}
                    onScroll={handleMainScroll}
                  >
                    <div style={styles.documentStack}>
                      {pages.map((page) => {
                        const pageMeta = loadedPageMeta[page.pageNumber];

                        return (
                          <section
                            key={page.pageNumber}
                            ref={(node) => {
                              pageSectionRefs.current[page.pageNumber] = node;
                            }}
                            style={styles.documentPageSection}
                          >
                            <div style={styles.documentPageTag}>Page {page.pageNumber}</div>
                            <canvas
                              ref={(node) => {
                                canvasRefs.current[page.pageNumber] = node;
                              }}
                              style={{
                                ...styles.canvas,
                                width: pageMeta
                                  ? `${Math.max(480, Math.round(pageMeta.width * zoom))}px`
                                  : "820px",
                              }}
                              onPointerDown={handlePointerDown(page.pageNumber)}
                              onPointerMove={handlePointerMove(page.pageNumber)}
                              onPointerUp={handlePointerUp(page.pageNumber)}
                              onPointerCancel={handlePointerCancel}
                            />
                          </section>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>

        {isScreen ? <div style={styles.screenFileBadge}>{fileName}</div> : null}
        <div style={footerStyle}>
          {!isScreen ? <div style={fileHintStyle}>{fileName}</div> : null}
          <div style={styles.actionRow}>
            <button type="button" style={cancelButtonStyle} onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...submitButtonStyle,
                ...(busy || loading || !!error ? styles.disabledButton : {}),
              }}
              onClick={() => onConfirm(allRectangles)}
              disabled={busy || loading || !!error}
            >
              {busy ? "Preparing..." : "Download PDF"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    background: "rgba(28, 32, 42, 0.68)",
    backdropFilter: "blur(8px)",
    padding: 12,
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    overflow: "hidden",
  },
  screenShell: {
    width: "100%",
    margin: 0,
    height: "100%",
    minHeight: "100%",
    overflow: "hidden",
  },
  card: {
    width: "min(1460px, 100%)",
    height: "calc(100vh - 24px)",
    overflow: "hidden",
    borderRadius: 32,
    background: "linear-gradient(180deg, #fffdfb 0%, #fff8f4 100%)",
    boxShadow: "0 30px 90px rgba(28, 32, 42, 0.32)",
    border: "1px solid rgba(224, 186, 176, 0.9)",
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr) auto",
  },
  screenCard: {
    width: "100%",
    height: "100%",
    minHeight: "100%",
    maxHeight: "none",
    borderRadius: 0,
    border: "none",
    boxShadow: "none",
    background: "linear-gradient(180deg, #fffdfb 0%, #fff8f4 100%)",
    position: "relative",
  },
  viewerToolbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "center",
    flexWrap: "wrap",
    padding: "18px 24px",
    borderBottom: "1px solid rgba(224, 186, 176, 0.7)",
    background:
      "radial-gradient(circle at top left, rgba(214, 92, 60, 0.10) 0, transparent 20%), rgba(255,253,251,0.98)",
  },
  viewerToolbarScreen: {
    padding: "8px 18px 10px",
    alignItems: "center",
  },
  viewerBrand: {
    minWidth: 0,
  },
  screenBackButton: {
    border: "none",
    background: "transparent",
    color: "#b13d2a",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    padding: 0,
    margin: "0 0 10px",
  },
  viewerEyebrow: {
    margin: 0,
    fontSize: 10,
    letterSpacing: 2.1,
    textTransform: "uppercase",
    color: "#b13d2a",
    fontWeight: 900,
  },
  viewerEyebrowScreen: {
    color: "#b13d2a",
  },
  viewerTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 6,
  },
  viewerTitle: {
    margin: 0,
    fontSize: 20,
    lineHeight: 1.2,
    color: "#252935",
    fontWeight: 950,
  },
  viewerTitleScreen: {
    color: "#252935",
  },
  viewerControls: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  controlGroup: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    borderRadius: 999,
    background: "#fff",
    border: "1px solid rgba(224, 186, 176, 0.82)",
    boxShadow: "0 8px 18px rgba(177, 61, 42, 0.06)",
  },
  iconButton: {
    width: 34,
    height: 34,
    borderRadius: 12,
    border: "1px solid rgba(224, 186, 176, 0.82)",
    background: "#fff",
    color: "#5f6679",
    fontSize: 20,
    fontWeight: 900,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
  },
  pageIndicator: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 4,
    minWidth: 70,
    justifyContent: "center",
    color: "#252935",
    fontSize: 14,
    fontWeight: 900,
  },
  zoomIndicator: {
    minWidth: 54,
    textAlign: "center",
    color: "#252935",
    fontSize: 14,
    fontWeight: 900,
  },
  modeButton: {
    border: "1px solid rgba(37,41,53,0.9)",
    background: "#fff",
    color: "#6d7385",
    borderRadius: 999,
    padding: "9px 16px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },
  modeButtonActive: {
    color: "#252935",
    boxShadow: "0 8px 18px rgba(37,41,53,0.08)",
  },
  viewerToolbarActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  toolbarPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "9px 13px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(224, 186, 176, 0.82)",
    color: "#5f6679",
    fontSize: 13,
    fontWeight: 900,
  },
  toolbarPillScreen: {
    background: "rgba(255,255,255,0.94)",
    borderColor: "rgba(224, 186, 176, 0.82)",
    color: "#5f6679",
  },
  toolbarHint: {
    color: "#8c6470",
    fontSize: 12,
    fontWeight: 800,
  },
  toolbarHintScreen: {
    color: "#8c6470",
  },
  body: {
    overflow: "hidden",
    padding: 0,
    background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,248,245,0.96) 100%)",
    display: "grid",
    gridTemplateColumns: "230px minmax(0, 1fr)",
    minHeight: 0,
  },
  bodyScreen: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,248,245,0.96) 100%)",
    gridTemplateColumns: "210px minmax(0, 1fr)",
  },
  sidebar: {
    borderRight: "1px solid rgba(224, 186, 176, 0.65)",
    background: "linear-gradient(180deg, #fffaf7 0%, #fff5f1 100%)",
    minHeight: 0,
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr) auto",
  },
  sidebarScreen: {
    borderRight: "1px solid rgba(224, 186, 176, 0.65)",
    background: "linear-gradient(180deg, #fffaf7 0%, #fff5f1 100%)",
  },
  sidebarHead: {
    padding: "16px 16px 12px",
    borderBottom: "1px solid rgba(224, 186, 176, 0.65)",
  },
  sidebarHeadScreen: {
    borderBottom: "1px solid rgba(224, 186, 176, 0.65)",
    padding: "12px 12px 10px",
  },
  sidebarLabel: {
    color: "#252935",
    fontSize: 13,
    fontWeight: 950,
  },
  sidebarLabelScreen: {
    color: "#252935",
  },
  sidebarHint: {
    marginTop: 4,
    color: "#8c6470",
    fontSize: 11,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  sidebarHintScreen: {
    color: "#8c6470",
  },
  thumbnailList: {
    overflowY: "auto",
    padding: 10,
    display: "grid",
    gap: 10,
    alignContent: "start",
  },
  sidebarFooter: {
    padding: "0 12px 12px",
    display: "grid",
    gap: 8,
    borderTop: "1px solid rgba(224, 186, 176, 0.5)",
    background: "linear-gradient(180deg, rgba(255,250,247,0) 0%, rgba(255,250,247,0.96) 18%)",
  },
  sidebarStatus: {
    color: "#8c6470",
    fontSize: 11,
    fontWeight: 800,
    textAlign: "center",
    paddingTop: 8,
  },
  thumbnailButton: {
    border: "1px solid rgba(224, 186, 176, 0.72)",
    borderRadius: 16,
    background: "#fff",
    padding: 9,
    cursor: "pointer",
    textAlign: "left",
    boxShadow: "0 12px 26px rgba(177, 61, 42, 0.05)",
  },
  thumbnailButtonActive: {
    borderColor: "#b13d2a",
    boxShadow: "0 16px 30px rgba(177, 61, 42, 0.16)",
    transform: "translateY(-1px)",
  },
  thumbnailPreviewWrap: {
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid rgba(224, 186, 176, 0.6)",
    background: "#f5efeb",
  },
  thumbnailPreview: {
    display: "block",
    width: "100%",
    height: "140px",
    objectFit: "cover",
    objectPosition: "top center",
    background: "#fff",
  },
  thumbnailMeta: {
    marginTop: 10,
    display: "grid",
    gap: 4,
  },
  thumbnailTitle: {
    color: "#252935",
    fontSize: 13,
    fontWeight: 900,
  },
  thumbnailSub: {
    color: "#8c6470",
    fontSize: 11,
    fontWeight: 800,
  },
  viewerMain: {
    minWidth: 0,
    minHeight: 0,
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr)",
  },
  viewerMetaBar: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "center",
    flexWrap: "wrap",
    padding: "16px 20px",
    borderBottom: "1px solid rgba(224, 186, 176, 0.6)",
    background: "rgba(255,255,255,0.78)",
  },
  viewerMetaBarScreen: {
    borderBottom: "1px solid rgba(224, 186, 176, 0.6)",
    background: "rgba(255,255,255,0.78)",
    padding: "8px 14px",
  },
  viewerMetaActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  pageTitle: {
    color: "#252935",
    fontSize: 15,
    fontWeight: 900,
  },
  pageTitleScreen: {
    color: "#252935",
  },
  pageMeta: {
    marginTop: 4,
    color: "#8c6470",
    fontSize: 12,
    fontWeight: 700,
  },
  pageMetaScreen: {
    color: "#8c6470",
  },
  viewerStage: {
    minHeight: 0,
    padding: 18,
    background:
      "linear-gradient(180deg, rgba(248,241,238,0.96) 0%, rgba(245,236,232,0.96) 100%)",
  },
  viewerStageScreen: {
    padding: "6px 8px 8px",
    background:
      "linear-gradient(180deg, rgba(248,241,238,0.96) 0%, rgba(245,236,232,0.96) 100%)",
  },
  canvasWrap: {
    overflow: "auto",
    height: "100%",
    borderRadius: 24,
    border: "1px solid rgba(224, 186, 176, 0.72)",
    background: "linear-gradient(180deg, #f6eeea 0%, #f1e6e2 100%)",
    padding: 20,
    boxSizing: "border-box",
  },
  canvasWrapScreen: {
    border: "1px solid rgba(224, 186, 176, 0.72)",
    borderRadius: 20,
    background: "linear-gradient(180deg, #f6eeea 0%, #f1e6e2 100%)",
    padding: "8px 8px 78px",
    height: "100%",
  },
  documentStack: {
    minHeight: "100%",
    display: "grid",
    gap: 28,
    justifyItems: "center",
    alignContent: "start",
  },
  documentPageSection: {
    display: "grid",
    gap: 10,
    justifyItems: "center",
  },
  documentPageTag: {
    alignSelf: "stretch",
    justifySelf: "start",
    color: "#b7bdc9",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
  },
  canvas: {
    display: "block",
    height: "auto",
    maxWidth: "none",
    background: "#ffffff",
    cursor: "crosshair",
    borderRadius: 14,
    touchAction: "none",
    boxShadow: "0 24px 60px rgba(37,41,53,0.14)",
  },
  footer: {
    display: "grid",
    gap: 12,
    padding: "0 32px 24px",
    borderTop: "1px solid rgba(224, 186, 176, 0.7)",
    background: "rgba(255,253,251,0.98)",
    boxShadow: "0 -16px 30px rgba(177, 61, 42, 0.05)",
  },
  footerScreen: {
    position: "absolute",
    right: 18,
    bottom: 18,
    zIndex: 8,
    border: "1px solid rgba(224, 186, 176, 0.9)",
    background: "rgba(255,253,251,0.96)",
    boxShadow: "0 18px 40px rgba(37,41,53,0.16)",
    backdropFilter: "blur(10px)",
    borderRadius: 24,
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "nowrap",
  },
  fileHint: {
    paddingTop: 16,
    color: "#6d7385",
    fontSize: 12,
    fontWeight: 800,
    wordBreak: "break-word",
  },
  fileHintScreen: {
    color: "#6d7385",
  },
  screenFileBadge: {
    position: "absolute",
    left: 16,
    bottom: 22,
    zIndex: 7,
    maxWidth: "min(42vw, 520px)",
    color: "#6d7385",
    fontSize: 11,
    fontWeight: 800,
    padding: "10px 14px",
    borderRadius: 18,
    border: "1px solid rgba(224, 186, 176, 0.82)",
    background: "rgba(255,253,251,0.92)",
    boxShadow: "0 16px 34px rgba(37,41,53,0.10)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  actionRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  cancelButton: {
    border: "1px solid rgba(224, 186, 176, 0.9)",
    background: "#fff",
    color: "#6d7385",
    borderRadius: 999,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 900,
    cursor: "pointer",
  },
  submitButton: {
    border: "none",
    background: "#252935",
    color: "#fff",
    borderRadius: 999,
    padding: "12px 22px",
    fontSize: 14,
    fontWeight: 950,
    cursor: "pointer",
  },
  submitButtonScreen: {
    background: "#252935",
    color: "#fff",
    boxShadow: "0 16px 34px rgba(185,52,58,0.18)",
  },
  ghostButton: {
    border: "1px solid rgba(224, 186, 176, 0.9)",
    background: "#fff",
    color: "#b13d2a",
    borderRadius: 999,
    padding: "9px 14px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },
  ghostButtonScreen: {
    borderColor: "rgba(224, 186, 176, 0.9)",
    background: "#fff",
    color: "#b13d2a",
  },
  cancelButtonScreen: {
    borderColor: "rgba(224, 186, 176, 0.9)",
    background: "#fff",
    color: "#6d7385",
  },
  disabledButton: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  emptyState: {
    gridColumn: "1 / -1",
    padding: "38px 22px",
    margin: 20,
    textAlign: "center",
    borderRadius: 18,
    background: "#fff8f5",
    border: "1px solid rgba(224, 186, 176, 0.7)",
    color: "#6d7385",
    fontSize: 14,
    fontWeight: 700,
  },
  errorState: {
    gridColumn: "1 / -1",
    padding: "22px",
    margin: 20,
    textAlign: "center",
    borderRadius: 18,
    background: "#fff3f0",
    border: "1px solid rgba(210, 58, 43, 0.28)",
    color: "#b13d2a",
    fontSize: 14,
    fontWeight: 800,
  },
  inlineError: {
    color: "#b13d2a",
    background: "#fff3f0",
    border: "1px solid rgba(210, 58, 43, 0.18)",
    borderRadius: 12,
    padding: "10px 12px",
    fontSize: 11,
    lineHeight: 1.5,
    fontWeight: 800,
  },
  close: {
    border: "none",
    background: "transparent",
    color: "#6d7385",
    fontSize: 24,
    fontWeight: 900,
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
  },
};
